import { Alert, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useIsMobileViewport } from '../../../../hooks/ui/useIsMobileViewport';
import { CollectionCreateForm } from '../collection/CollectionCreateForm';
import { DestinationPickerResults } from '../destination/DestinationPickerResults';
import { useCreateDestinationResults } from '../destination/useCreateDestinationResults';
import { workspaceDestination, type CreateDestination } from '../destination/createDestination';
import { useWorkspace } from '../../../../contexts/WorkspaceContext';

interface CreatePickerViewProps {
  /** What is chosen now (marked selected). */
  readonly selected: CreateDestination;
  /** Why the picker opened by itself, e.g. the destination was deleted. */
  readonly message?: string;
  readonly onPick: (destination: CreateDestination, how: 'picker' | 'new_collection') => void;
  /** The inline New collection form is showing (held by the modal, so Back can close it). */
  readonly creating: boolean;
  readonly onCreatingChange: (creating: boolean) => void;
}

/**
 * "Save to…" inside the modal: the same results list Capture uses, plus
 * view-only rows shown disabled, "Inside {parent}", the lock, paging, and
 * "Create new collection" with the same form as the New collection step.
 */
export function CreatePickerView({
  selected,
  message,
  onPick,
  creating,
  onCreatingChange,
}: CreatePickerViewProps) {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobileViewport();
  const results = useCreateDestinationResults(query);
  const { workspace } = useWorkspace();
  // Focus the search on desktop only: on a phone the keyboard would cover the list.
  useEffect(() => {
    if (!isMobile && !creating) searchRef.current?.focus();
  }, [isMobile, creating]);

  if (creating) {
    return (
      <CollectionCreateForm
        // After a refusal, `selected` is the place that refused: don't
        // create inside it again, start at the workspace root.
        parent={message && workspace ? workspaceDestination(workspace) : selected}
        initialName={query.trim()}
        inline
        onChangeParent={() => onCreatingChange(false)}
        onUseExisting={(c) => onPick({ type: 'project', id: c.id, name: c.name }, 'picker')}
        onCreated={(c) =>
          onPick(
            { type: 'project', id: c.id, name: c.name, privacyMode: c.privacyMode, canEdit: true },
            'new_collection',
          )
        }
      />
    );
  }

  return (
    <VStack align="stretch" spacing={3} h="full" minH={0}>
      {message && (
        <Alert status="warning" borderRadius="lg">
          <Text fontSize="sm">{message}</Text>
        </Alert>
      )}
      <DestinationPickerResults
        collectionDestinations={results.collections}
        visibleWorkspace={results.root}
        noteDestinations={[]}
        showNotes={false}
        isLoading={results.isLoading}
        isNotesLoading={false}
        isSearching={results.isSearching}
        hasMore={results.hasMore}
        isLoadingMore={results.isLoadingMore}
        onLoadMore={results.loadMore}
        onCreate={() => onCreatingChange(true)}
        onQueryChange={setQuery}
        onSelect={(d) => d.type !== 'note' && onPick(d, 'picker')}
        query={query}
        searchRef={searchRef}
        scrollContainerRef={scrollRef}
        selectedDestination={selected}
      />
    </VStack>
  );
}
